import { ChangeEvent, useEffect, useState, FocusEvent, Dispatch, useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Link } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import cn from 'classnames';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { AutoComplete } from 'UIPackage/MainModule/components/AutoComplete';
import { IKnmIndex } from 'APIPackage/KnmModule/interfaces/IKnmIndex';
import { IKnmLink } from 'APIPackage/KnmModule/interfaces/IKnmLink';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { ROUTE_DECISION, ROUTE_KNM } from 'CorePackage/MainModule/routes/routes';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { formatDate, formatDateLocale } from 'CorePackage/MainModule/common/formatDate';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  knmListFindKnm,
  knmListFindKnmForDecisionReason,
  knmListSetFindData,
} from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListFindData } from 'KnmListPackage/MainModule/selectors/knmListSelectors';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import {
  getDecisionListData,
  getDecisionListSearchValue,
} from 'DecisionListPackage/MainModule/selectors/decisionListSelectors';
import { IDecisionListItem } from 'DecisionListPackage/MainModule/interfaces/IDecisionListItem';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';
import {
  decisionListGetData,
  decisionListSetSearchValue,
} from 'DecisionListPackage/MainModule/actions/decisionListActions';
import { fetchDecisionById } from 'APIPackage/DecisionModule/services/decisionApi';
import { IDecision } from 'APIPackage/DecisionModule/interfaces/IDecision';
import { KindResultDecisionCodesEnum } from 'APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import {
  decisionFillOrganizationsAndObjectsByOtherPassport,
  decisionSetPrescriptionOldRules,
} from 'DecisionPackage/MainModule/actions/decisionActions';
import { fetchKnm } from 'APIPackage/KnmModule/services/knmApi';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IDecisionOrganization } from 'APIPackage/DecisionModule/interfaces/IDecisionOrganization';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';

import styles from './DecisionReasonLinks.module.scss';
import {
  hasDecisionPrescription,
  hasKnmPrescription,
  isSourceCreatedByOldRules,
  LEGACY_NO_PREFILL,
  LinkValidationNoPrescriptionMessageText,
} from '../../../../CorePackage/MainModule/common/prescriptionLegacy';
import { fetchDictValue } from '../../../../CorePackage/DictionariesModule/common/fetchDictValue';
import { IKnoOrganizationDictionary } from '../../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';
import { ISupervisionType } from '../../../../APIPackage/DictionariesModule/interfaces/ISupervisionType';

const ERP_ID_MIN_LENGTH = 12;

interface IKnmAutocompleteOption {
  label: string;
  value: string;
  knm?: IKnmIndex;
  decision?: IDecisionListItem;
}

const generateKnmInfoText = (knm: IKnmIndex) =>
  `${knm.erpId ?? ''} ${knm.knmType ?? ''} ${knm.organizationName ?? ''} ${knm.inn ? `(ИНН: ${knm.inn}` : ''}${
    knm.ogrn ? ` ОГРН: ${knm.ogrn}` : ''
  }) ${knm.controllingOrganization ?? ''}${knm.prosecutorOrganization ?? ''} ${formatDateLocale({
    date: knm.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knm.stopDate ? `${formatDateLocale({ date: knm.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

const generateDecisionInfoText = (decision: IDecisionListItem) =>
  `${decision.number ?? ''} ${decision.organizationName ?? ''} ${decision.kno ?? ''}${
    decision.prosecutorOrganization ?? ''
  } ${formatDateLocale({
    date: decision.decisionDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })}}`;

const generateLinkInfoText = (knmLink: IKnmLink) =>
  `${knmLink.subjectName ?? ''} ${knmLink.inn ? `(ИНН: ${knmLink.inn})` : ''}${
    knmLink.ogrn ? ` (ОГРН: ${knmLink.ogrn})` : ''
  } ${knmLink.knoOrganizationName ?? ''} ${knmLink.prosecutorOfficeName ?? ''} ${formatDate({
    date: knmLink.startDate,
    dateFormat: DATE_FORMAT_LONG_FNS,
  })} - ${knmLink.stopDate ? `${formatDate({ date: knmLink.stopDate, dateFormat: DATE_FORMAT_LONG_FNS })}` : '?'}`;

const LinkValidationGeneralMessageText = 'Паспорт не удовлетворяет условиям';

interface IDecisionReasonLinksProps {
  watchOnly: boolean;
  isEnabled: boolean;
  linksName?: string;
  created: string | undefined;
  reason: IDecisionFormReason;
  kindControlCode: string;
  controllingOrganizationIds: string;
}

interface IDecisionReasonLinksStoreProps {
  formRef: IDecisionFormRef | undefined;
  knms: IKnmIndex[];
  settings: IAdminSetting[];
  decisions: IDecisionListItem[];
  searchValue: string | undefined;
}

const DecisionReasonLinksWithoutStore = ({
  watchOnly,
  isEnabled,
  linksName,
  formRef,
  knms,
  settings,
  created,
  decisions,
  reason,
  kindControlCode,
  controllingOrganizationIds,
  searchValue,
}: IDecisionReasonLinksProps & IDecisionReasonLinksStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isPrescriptionCheck = reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK;

  const [knmsAutocompleteOptions, setKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[] | undefined>();
  const [erpIdInputValue, setErpIdInputValue] = useState('');
  const [isErpIdSelected, setIsErpIdSelected] = useState(false);
  const [lastKnmsAutocompleteOptions, setLastKnmsAutocompleteOptions] = useState<IKnmAutocompleteOption[]>([]);
  const [isFounded, setIsFounded] = useState(false);

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isEnablePrescriptionCheck = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_105_166_25_4_1_30_STARTS_FROM,
  );

  useEffect(() => {
    return () => {
      dispatch(decisionListSetSearchValue('', undefined));
    };
  }, []);

  function findPassports(paramErpId?: string) {
    if (paramErpId && paramErpId.length >= ERP_ID_MIN_LENGTH) {
      setIsFounded(true);

      if (isEnablePaperlessDocument && isPrescriptionCheck) {
        dispatch(
          decisionListSetSearchValue(paramErpId, {
            resultDecisionsKind: ['D_7', 'D_1'],
            controlKindCode: kindControlCode,
            knoIds: [controllingOrganizationIds],
          }),
        );
        dispatch(decisionListGetData());
      } else {
        dispatch(
          knmListFindKnmForDecisionReason(
            { erpId: paramErpId, version: KnmVersionsEnum.ERKNM, resultDecisionsKind: ['D_7'] },
            2000,
          ),
        );
      }
    }
  }

  const debouncedFindPassports = debounce(findPassports, 2000);

  const handleTextInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      const valueEdited = value?.trim();

      setErpIdInputValue(valueEdited);
      if (!valueEdited) {
        setIsErpIdSelected(false);
      }
      debouncedFindPassports(valueEdited);
      if (!valueEdited || valueEdited.length <= ERP_ID_MIN_LENGTH) {
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedFindPassports],
  );

  const addLink = useCallback(
    (knm?: IKnmIndex, decision?: IDecisionListItem) => {
      if (push) {
        if (decision) {
          validateSelectedLinkDecision(decision).then(valRes => {
            const link = {
              erpId: decision.number,
              id: decision.id,
              knmType: 'Decision',
              subjectName: decision.organizationName,
              inn: '',
              ogrn: '',
              knoOrganizationName: decision.kno,
              prosecutorOfficeName: decision.prosecutorOrganization,
              startDate: formatDateLocale({ date: decision.decisionDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
              stopDate: null,
            } as IKnmLink;

            push(linksName || 'links', link);

            dispatch(decisionSetPrescriptionOldRules(valRes === LEGACY_NO_PREFILL));

            if (typeof valRes === 'object') {
              dispatch(decisionFillOrganizationsAndObjectsByOtherPassport(valRes, link));
            }
          });
        } else if (isEnablePrescriptionCheck && isPrescriptionCheck && knm) {
          validateSelectedLinkKnm(knm).then(valRes => {
            const link = {
              erpId: knm.erpId,
              id: knm.id,
              knmType: knm.knmType,
              subjectName: knm.organizationName,
              inn: knm.inn,
              ogrn: knm.ogrn,
              knoOrganizationName: knm.controllingOrganization,
              prosecutorOfficeName: knm.prosecutorOrganization,
              startDate: formatDateLocale({ date: knm.startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }),
              stopDate: knm.stopDate
                ? formatDateLocale({ date: knm.stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
                : null,
            } as IKnmLink;

            push(linksName || 'links', link);

            dispatch(decisionSetPrescriptionOldRules(valRes === LEGACY_NO_PREFILL));

            if (typeof valRes === 'object') {
              dispatch(decisionFillOrganizationsAndObjectsByOtherPassport(valRes, link));
            }
          });
        } else {
          push(linksName || 'links', {
            erpId: knm?.erpId,
            id: knm?.id,
            knmType: knm?.knmType,
            subjectName: knm?.organizationName,
            inn: knm?.inn,
            ogrn: knm?.ogrn,
            knoOrganizationName: knm?.controllingOrganization,
            prosecutorOfficeName: knm?.prosecutorOrganization,
            startDate: formatDateLocale({ date: knm?.startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }),
            stopDate: knm?.stopDate
              ? formatDateLocale({ date: knm?.stopDate, dateFormat: SYSTEM_DATE_FORMAT_FNS })
              : null,
          });
        }
      }
      setErpIdInputValue('');
      setIsErpIdSelected(false);
    },
    [push],
  );

  const validateSelectedLinkDecision = useCallback(
    async (decision: IDecisionListItem) => {
      const selectedLinkDecisionData: IDecision = await fetchDecisionById(decision.id);

      if (reason?.type?.name === KnmReasonTypeNameEnum.PRESCRIPTION_CHECK && isEnablePaperlessDocument) {
        if (isSourceCreatedByOldRules(selectedLinkDecisionData.createdAt, settings)) {
          if (!hasDecisionPrescription(selectedLinkDecisionData)) {
            return LinkValidationNoPrescriptionMessageText;
          }

          return LEGACY_NO_PREFILL;
        }

        if (selectedLinkDecisionData.organization?.decisions) {
          if (selectedLinkDecisionData.organization?.decisions?.length) {
            const prescription = selectedLinkDecisionData.organization?.decisions.find(
              i => i.kind?.code === KindResultDecisionCodesEnum.D_1 || i.kind?.code === KindResultDecisionCodesEnum.D_7,
            );

            if (!prescription) {
              return LinkValidationGeneralMessageText;
            }

            if (selectedLinkDecisionData.organization && selectedLinkDecisionData.objects) {
              return {
                organizations: [selectedLinkDecisionData.organization],
                objects: selectedLinkDecisionData.objects,
                events: null,
                requirements: null,
              };
            }

            return LinkValidationGeneralMessageText;
          }

          return LinkValidationGeneralMessageText;
        }
      }

      return undefined;
    },
    [reason, reason?.type?.name, isEnablePaperlessDocument, settings],
  );

  const validateSelectedLinkKnm = useCallback(
    async (knm: IKnmIndex) => {
      const knmId = knm.id;
      const selectedLinkKnmData: IKnm = await fetchKnm(knmId);

      if (isPrescriptionCheck && isEnablePaperlessDocument) {
        if (isSourceCreatedByOldRules(selectedLinkKnmData.created, settings)) {
          const knoDictValue = await fetchDictValue<IKnoOrganizationDictionary>(
            selectedLinkKnmData.pmErknm
              ? selectedLinkKnmData.pmErknm.knoOrganization
              : selectedLinkKnmData.knmErknm?.knoOrganization,
          );

          if (knoDictValue && knoDictValue.value.idStr?.toString() !== controllingOrganizationIds) {
            return LinkValidationNoPrescriptionMessageText;
          }

          const sourceSupervisionType = selectedLinkKnmData.pmErknm
            ? selectedLinkKnmData.pmErknm.supervisionType
            : selectedLinkKnmData.knmErknm?.kindControl;

          const supervisionDictValue = await fetchDictValue<ISupervisionType>(sourceSupervisionType);
          const sourceCode = supervisionDictValue?.value.code;

          if (kindControlCode && sourceCode && sourceCode !== kindControlCode) {
            return LinkValidationNoPrescriptionMessageText;
          }

          if (!hasKnmPrescription(selectedLinkKnmData)) {
            return LinkValidationNoPrescriptionMessageText;
          }

          return LEGACY_NO_PREFILL;
        }
        if (selectedLinkKnmData.pmErknm) {
          if (selectedLinkKnmData.pmErknm.subject?.pmResultDecisions?.length) {
            const prescriptions = selectedLinkKnmData.pmErknm.subject?.pmResultDecisions.filter(
              i =>
                i.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
                i.kindDecision?.code === KindResultDecisionCodesEnum.D_7,
            );

            if (!prescriptions.length) {
              return LinkValidationGeneralMessageText;
            }

            if (selectedLinkKnmData.pmErknm?.subject && selectedLinkKnmData.pmErknm?.objects) {
              return {
                organizations: [
                  {
                    ...selectedLinkKnmData.pmErknm?.subject,
                    name: selectedLinkKnmData.pmErknm?.subject.organizationName,
                    organizationType: selectedLinkKnmData.pmErknm?.subject.subjectType,
                    originalName: null,
                    originalNameReactQuillEditor: null,
                    isForeignSubjectAgencyNotRegisteredInRf: null,
                    decisions: null,
                  } as IDecisionOrganization,
                ],
                objects: selectedLinkKnmData.pmErknm?.objects,
                requirements: null,
                events: null,
              };
            }

            return LinkValidationGeneralMessageText;
          }

          return LinkValidationGeneralMessageText;
        }
        if (selectedLinkKnmData.knmErknm) {
          if (selectedLinkKnmData?.knmErknm?.organizations?.[0]?.resultDecisions) {
            const prescriptions = selectedLinkKnmData?.knmErknm?.organizations?.[0]?.resultDecisions.filter(
              i =>
                i.kindDecision?.code === KindResultDecisionCodesEnum.D_1 ||
                i.kindDecision?.code === KindResultDecisionCodesEnum.D_7,
            );

            if (!prescriptions.length) {
              return LinkValidationGeneralMessageText;
            }

            if (selectedLinkKnmData.knmErknm?.organizations && selectedLinkKnmData.knmErknm?.objects) {
              return {
                organizations: selectedLinkKnmData.knmErknm?.organizations.map(org => {
                  return {
                    ...org,
                    name: org.organizationName,
                    organizationType: org.subjectType,
                    originalName: null,
                    originalNameReactQuillEditor: null,
                    isForeignSubjectAgencyNotRegisteredInRf: null,
                    decisions: null,
                  } as IDecisionOrganization;
                }),
                objects: selectedLinkKnmData.knmErknm?.objects,
                requirements: null,
                events: null,
              };
            }

            return LinkValidationGeneralMessageText;
          }

          return LinkValidationGeneralMessageText;
        }
      }

      return undefined;
    },
    [reason, isEnablePaperlessDocument, reason?.type?.name, controllingOrganizationIds, settings],
  );

  const handleSaveButtonClick = useCallback(
    (value: string) => {
      const knmAutocompleteOption = lastKnmsAutocompleteOptions?.find(
        lastKnmAutocompleteOption => lastKnmAutocompleteOption.value === value,
      );

      const knm = knmAutocompleteOption?.knm;

      if (isEnablePaperlessDocument && isPrescriptionCheck) {
        const decision = knmAutocompleteOption?.decision;

        if (decision) {
          addLink(undefined, decision);
        } else {
          addLink(knm);
        }
      } else if (knm) {
        addLink(knm);
      }
    },
    [addLink, lastKnmsAutocompleteOptions, isPrescriptionCheck],
  );

  const handleAutoCompleteSelect = useCallback(
    (value: string) => () => {
      setErpIdInputValue(value);
      debouncedFindPassports('');
      setKnmsAutocompleteOptions(undefined);
      dispatch(decisionListSetSearchValue('', undefined));
      setIsErpIdSelected(true);
      handleSaveButtonClick(value);
    },
    [debouncedFindPassports, handleSaveButtonClick],
  );

  useEffect(() => {
    if (!decisions?.length && isEnablePrescriptionCheck && isPrescriptionCheck && searchValue) {
      dispatch(
        knmListFindKnm(
          {
            searchString: searchValue,
            supervisionTypeId: kindControlCode,
            isKnmLinkQuery: true,
            kinds: [
              'Профилактический визит',
              'Выборочный контроль',
              'Мониторинговая закупка',
              'Контрольная закупка',
              'Инспекционный визит',
              'Документарная проверка',
              'Выездная проверка',
            ],
          },
          2000,
        ),
      );
    }
  }, [decisions]);

  useEffect(() => {
    if (isFounded) {
      let options: IKnmAutocompleteOption[] | undefined;

      if (isEnablePaperlessDocument && isPrescriptionCheck) {
        if (decisions?.length) {
          options = decisions.map(decision => {
            const decisionText = `${generateDecisionInfoText(decision)}`;

            return {
              value: decisionText,
              label: decisionText,
              decision,
            };
          });
        } else {
          options = knms.map(knm => {
            const knmText = `${generateKnmInfoText(knm)}`;

            return {
              value: knmText,
              label: knmText,
              knm,
            };
          });
        }
      } else {
        options = knms.map(knm => {
          const knmText = `${generateKnmInfoText(knm)}`;

          return {
            value: knmText,
            label: knmText,
            knm,
          };
        });
      }

      setKnmsAutocompleteOptions(options);
    }
  }, [knms, isFounded, decisions]);

  useUnmount(() => {
    dispatch(knmListSetFindData([]));
  });

  useEffect(() => {
    knmsAutocompleteOptions && setLastKnmsAutocompleteOptions(knmsAutocompleteOptions);
  }, [knmsAutocompleteOptions]);

  const handleTextInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const tagName = (e?.relatedTarget as Element)?.tagName;

      if (tagName !== 'UL') {
        debouncedFindPassports('');
        setKnmsAutocompleteOptions(undefined);
      }
    },
    [debouncedFindPassports],
  );

  const handleTextInputClear = useCallback(() => {
    setErpIdInputValue('');
    setIsErpIdSelected(false);
  }, []);

  const deleteLink = (linkedErpId: string) => {
    // @ts-ignore
    const formLinks: IKnmLink[] | undefined = formRef?.current.getFieldState(linksName || 'links')?.value;
    const deletingLinkIndex = formLinks?.findIndex(formLink => formLink.erpId === linkedErpId);

    dispatch(decisionSetPrescriptionOldRules(false));
    formRef?.current?.change('objects', undefined);
    formRef?.current?.change('organization', undefined);

    if (typeof deletingLinkIndex === 'number' && deletingLinkIndex >= 0 && remove) {
      remove(linksName || 'links', deletingLinkIndex);
    }
  };

  const makeLinkDeleteButtonClickHandler = (linkedId: string) => () => {
    if (!watchOnly && linkedId && isEnabled) {
      deleteLink(linkedId);
    }
  };

  return (
    <FieldArray name={linksName || 'links'} key={0}>
      {({ fields }) => {
        if (fields.length === 0) {
          return (
            <div>
              <div className={cn('shared-field-row', styles.LinkFields)}>
                <FieldBlock
                  label={
                    <>
                      Ссылка на другой паспорт <span className={styles.RedMark}> *</span>
                    </>
                  }
                  placementLabel="left"
                  color="black"
                >
                  <AutoComplete
                    type="area"
                    options={knmsAutocompleteOptions}
                    noFoundMessage="КНМ, соответствующие указанному учетному номеру, не найдены"
                    handleAutoCompleteSelect={handleAutoCompleteSelect}
                  >
                    <TextInput
                      id="linkErpId"
                      name="linkErpId"
                      placeholder="Введите номер паспорта, выберите из выпадающего списка"
                      clearable={isErpIdSelected}
                      value={erpIdInputValue}
                      onBlur={handleTextInputBlur}
                      onChange={handleTextInputChange}
                      onClear={handleTextInputClear}
                      disabled={watchOnly}
                    />
                  </AutoComplete>
                </FieldBlock>
              </div>
              <FormSpy subscription={{ errors: true, touched: true }}>
                {props => {
                  if (!linksName || !props.errors) return null;

                  const fieldError = getIn(props.errors, linksName);
                  const isFieldTouched = true;

                  if (isFieldTouched && fieldError) {
                    return (
                      <Row>
                        <p className="shared-input-error">{fieldError}</p>
                      </Row>
                    );
                  }

                  return null;
                }}
              </FormSpy>
            </div>
          );
        }

        return (
          <ul className={styles.Links}>
            {fields.map((name, index) => {
              const link = fields.value?.[index] as IKnmLink | undefined;
              const linkId = link?.id || '';

              return (
                <li className={styles.Link} key={name}>
                  <Link
                    className={styles.IdLink}
                    to={link?.knmType === 'Decision' ? ROUTE_DECISION(linkId) : ROUTE_KNM(linkId)}
                    target="blank"
                  >
                    {fields.value?.[index]?.erpId}
                  </Link>
                  <div className={styles.LinkText}>{generateLinkInfoText(fields.value?.[index])}</div>
                  <CloseButton
                    className={styles.LinkDeleteButton}
                    disabled={watchOnly || !isEnabled}
                    onClick={makeLinkDeleteButtonClickHandler(fields.value?.[index]?.erpId)}
                  />
                </li>
              );
            })}
          </ul>
        );
      }}
    </FieldArray>
  );
};

const mapStateToProps = (state: IRootState): IDecisionReasonLinksStoreProps => ({
  formRef: getDecisionFormRef(state),
  knms: getKnmListFindData(state),
  decisions: getDecisionListData(state),
  settings: getAdminFetchSettings(state),
  searchValue: getDecisionListSearchValue(state),
});

export const DecisionReasonLinks = connect(mapStateToProps)(DecisionReasonLinksWithoutStore);
